import React, { useEffect, useRef } from 'react';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import dagre from 'cytoscape-dagre';

cytoscape.use(fcose);
cytoscape.use(dagre);

export default function Graph({ elements, selected, onNodeClick }) {
    const cyRef = useRef(null);
    const containerRef = useRef(null);
    const onNodeClickRef = useRef(onNodeClick);
    onNodeClickRef.current = onNodeClick;
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    useEffect(() => {
        if (!containerRef.current) return;

        cyRef.current = cytoscape({
            container: containerRef.current,
            elements: [],
            style: [
                {
                    selector: 'node',
                    style: {
                        'label': 'data(id)',
                        'text-wrap': 'wrap',
                        'text-max-width': '60px',
                        'text-valign': 'center',
                        'text-halign': 'center',
                        'background-color': '#002366',
                        'color': '#EFBF04',
                        'font-size': '4px',
                        'width': 20,
                        'height': 20,
                        'border-width': 1,
                        'border-color': '#000'
                    }
                },
                {
                    selector: 'edge',
                    style: {
                        'curve-style': 'bezier',
                        'target-arrow-shape': 'triangle',
                        'label': 'data(label)',
                        'font-size': '4px',
                        'width': 1.5
                    }
                },
                {
                    // newer RFC replaces older one
                    selector: 'edge[type = "obsoletes"]',
                    style: {
                        'line-color': '#C1121F',
                        'target-arrow-color': '#C1121F'
                    }
                },
                {
                    // newer RFC adds to / supports older one (both still relevant)
                    selector: 'edge[type = "updates"]',
                    style: {
                        'line-color': '#006B3E',
                        'target-arrow-color': '#006B3E',
                        'line-style': 'solid'
                    }
                },
                {
                    selector: 'node:selected',
                    style: {
                        'border-color': '#EFBF04',
                        'border-width': 2
                    }
                }
            ],
            layout: { name: 'fcose' }
        });

        cyRef.current.on('tap', 'node', (e) => {
            const id = e.target.data('id');
            if (id && onNodeClickRef.current) onNodeClickRef.current(id);
        });

        const ro = new ResizeObserver(() => {
            if (cyRef.current) {
                cyRef.current.resize();
                cyRef.current.fit();
            }
        });
        ro.observe(containerRef.current);

        return () => {
            ro.disconnect();
            if (cyRef.current) { cyRef.current.destroy(); cyRef.current = null; }
        };
    }, []);

    useEffect(() => {
        if (!cyRef.current || !elements) return;
        const cy = cyRef.current;
        cy.elements().remove();
        cy.add(elements);

        // Radial layout: the focused RFC sits in the center, its relations form a
        // ring around it. Keeps the main RFC central even when all its relations
        // point one way (e.g. a current RFC that replaces/updates many others).
        const rootId = selectedRef.current?.data?.id;
        cy.layout({
            name: 'concentric',
            concentric: n => (n.data('id') === rootId ? 100 : 1),
            levelWidth: () => 1,
            minNodeSpacing: 25,
            padding: 30,
            fit: true,
            animate: false,
        }).run();

        // Cap only extreme zoom so a 2-node graph doesn't become absurdly huge.
        if (cy.zoom() > 4) { cy.zoom(4); cy.center(); }

        // Disable wheel zoom for small graphs; keep it for big hubs that need it.
        cy.userZoomingEnabled(cy.nodes().length > 15);
    }, [elements]);

    useEffect(() => {
        if (!cyRef.current || !selected) return;
        cyRef.current.$(':selected').unselect();
        const node = cyRef.current.getElementById(selected.data.id);
        if (node) node.select();
    }, [selected]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%', background: '#fafafa' }} />
        </div>
    );
}
